import type Ajv from "ajv";

import {
	getSchemaAlternatives,
	inferAlternativeIndex,
	SchemaAlternatives
} from "./alternatives";
import type { JsonSchemaDefinition } from "./types";

export interface AlternativeState extends SchemaAlternatives {
	index: number;
}

/** Editor selections are presentation state, separate from the submitted value. */
export class AlternativeContext {
	private readonly selections = new Map<JsonSchemaDefinition, Map<string, number>>();

	public get(
		schema: JsonSchemaDefinition,
		path: string,
		data: unknown,
		ajv: Ajv,
		root: JsonSchemaDefinition
	): AlternativeState | undefined {
		const alternatives = getSchemaAlternatives(schema, root);

		if(!alternatives) {
			return undefined;
		}

		const paths = this.selections.get(schema) ?? new Map<string, number>();
		const index = paths.get(path) ?? inferAlternativeIndex(alternatives, data, ajv, root);
		paths.set(path, index);
		this.selections.set(schema, paths);

		return {
			...alternatives,
			index
		};
	}

	public select(
		schema: JsonSchemaDefinition,
		path: string,
		index: number,
		reset = false,
		root: JsonSchemaDefinition = schema
	): void {
		if(reset) {
			for(const [candidateSchema, paths] of this.selections) {
				for(const candidate of paths.keys()) {
					const samePath = candidate === path;
					const descendant = path === "" || candidate.startsWith(`${path}.`);
					const ancestor = samePath && this.isAncestor(candidateSchema, schema, root);

					if((samePath || descendant) && !ancestor) {
						paths.delete(candidate);
					}
				}
			}
		}

		const paths = this.selections.get(schema) ?? new Map<string, number>();
		paths.set(path, index);
		this.selections.set(schema, paths);
	}

	public clear(): void {
		this.selections.clear();
	}

	public removeRow(path: string, index: number): void {
		const prefix = path ? `${path}.` : "";

		for(const [schema, paths] of this.selections) {
			const shifted = new Map<string, number>();

			for(const [candidate, selected] of paths) {
				if(!candidate.startsWith(prefix)) {
					shifted.set(candidate, selected);
					continue;
				}

				const [row, ...remaining] = candidate.slice(prefix.length).split(".");
				const rowIndex = Number(row);

				if(!/^\d+$/.test(row) || rowIndex < index) {
					shifted.set(candidate, selected);
				} else if(rowIndex > index) {
					shifted.set(`${prefix}${[rowIndex - 1, ...remaining].join(".")}`, selected);
				}
			}

			this.selections.set(schema, shifted);
		}
	}

	public clone(): AlternativeContext {
		const copy = new AlternativeContext();

		for(const [schema, paths] of this.selections) {
			copy.selections.set(schema, new Map(paths));
		}

		return copy;
	}

	public clearSubtree(path: string): void {
		const includes = (candidate: string): boolean =>
			path === "" || candidate === path || candidate.startsWith(`${path}.`);

		for(const paths of this.selections.values()) {
			for(const candidate of paths.keys()) {
				if(includes(candidate)) {
					paths.delete(candidate);
				}
			}
		}

	}

	public replaceSubtree(path: string, source: AlternativeContext): void {
		this.clearSubtree(path);
		const includes = (candidate: string): boolean =>
			path === "" || candidate === path || candidate.startsWith(`${path}.`);

		for(const [schema, sourcePaths] of source.selections) {
			const paths = this.selections.get(schema) ?? new Map<string, number>();

			for(const [candidate, index] of sourcePaths) {
				if(includes(candidate)) {
					paths.set(candidate, index);
				}
			}

			this.selections.set(schema, paths);
		}
	}

	private isAncestor(
		candidate: JsonSchemaDefinition,
		schema: JsonSchemaDefinition,
		root: JsonSchemaDefinition,
		visited = new Set<JsonSchemaDefinition>()
	): boolean {
		if(visited.has(candidate)) {
			return false;
		}

		visited.add(candidate);
		const alternatives = getSchemaAlternatives(candidate, root);

		return alternatives?.options.some(option =>
			option.schema === schema || this.isAncestor(option.schema, schema, root, visited)) ?? false;
	}
}
